// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import { CartLineCardCondensedStyles } from "@insite/content-library/Widgets/Cart/CartLineCardCondensed";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Clickable from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LinesHeader } from "@keller/App/Components/Cart/CartContainerLayout";
import XngLoadingOverlayWrapper from "@keller/App/Components/Common/XngLoadingOverlayWrapper";
import { xCommonLoadingOverlay } from "@keller/App/Handlers/XNG/Common";
import cartLineSharedStyles from "@keller/App/SharedStyle/Cart/CartLineSharedStyles";
import { getIsNonPurchasable, xIsAuthenticated } from "@keller/App/Store/Selectors/GeneralSelectors";
import CartLineCardExpanded, { CartLineCardExpandedStyles } from "@keller/Overrides/Widgets/Cart/CartLineCardExpanded";
import { constants } from "@keller/XDL/constants";
import { createLinkIconStyles, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import * as cssLinter from "css";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showLineNotes = "showLineNotes",
    showImage = "showImage",
    showProductTitle = "showProductTitle",
    showBrand = "showBrand",
    showRemoveAllActions = "showRemoveAllActions",
    showPerUnitPrice = "showPerUnitPrice",
    showPriceLabel = "showPriceLabel",
    showAddToList = "showAddToList",
    showItemCount = "showItemCount",
    showProductNumber = "showProductNumber",
    showBackToTop = "showBackToTop",
    showVmiBinDetails = "showVmiBinDetails",
    customCSS = "customCSS",
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};
const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};
const defaultCustomCss = `.main-grid-container{
}
.title-grid-item{
}
.title{
}
.description-grid-item{
}
.description-text{
}
.email-grid-item{
}
.email-text-field{
}
.email-button{
}
.disclaimer-grid-item{
}
.disclaimer-text{
}`;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showLineNotes]: boolean;
        [fields.showImage]: boolean;
        [fields.showProductTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showRemoveAllActions]: boolean;
        [fields.showPerUnitPrice]: boolean;
        [fields.showPriceLabel]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showItemCount]: boolean;
        [fields.showProductNumber]: boolean;
        [fields.showBackToTop]: boolean;
        [fields.showVmiBinDetails]: boolean;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    promotionsDataView: getCurrentPromotionsDataView(state),
    settingsCollection: getSettingsCollection(state),
    isClearingCart: state.pages.cart.isClearingCart,
    isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
    isLoading: state.data.carts.isLoading,
    isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
    showPricing: state.context.showPricing,
    showInventory: state.context.showInventory,
    isAuthenticated: xIsAuthenticated(state),
    isNonPurchasable: getIsNonPurchasable(state),
});

const mapDispatchToProps = {
    clearCurrentCart,
    updateCartLine,
    removeCartLine,
    xCommonLoadingOverlay,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CartLinesStyles {
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noCartLinesText?: TypographyProps;
    noCartLinesIcon?: IconPresentationProps;
    headerWrapper?: InjectableCss;
    header?: CartLinesHeaderStyles;
    cardList?: CardListStyles;
    cardContainer?: CardContainerStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    footerWrapper?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
    cartLinesActions?: InjectableCss;
    overflowMenu?: OverflowMenuPresentationProps;
    cartLineActionsWrapper?: InjectableCss;
}

interface CartLinesHeaderStyles {
    itemCountText?: TypographyProps;
    condensedViewCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    condensedViewCheckbox?: CheckboxPresentationProps;
    overflowMenu?: OverflowMenuPresentationProps;
    removeAllLink?: LinkPresentationProps;
    mainSection?: InjectableCss;
    warningIcon?: IconPresentationProps;
    warningSection?: InjectableCss;
    warningText?: InjectableCss;
}

export const cartLinesStyles: CartLinesStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    noCartLinesText: {
        variant: "h3",
    },
    noCartLinesIcon: {
        src: ShoppingCart,
        size: 45,
        color: "text.link",
        css: css`
            margin-bottom: 20px;
        `,
    },
    headerWrapper: {
        ...mergeWithCss(cartLineSharedStyles.header?.wrapper, {
            css: css`
                ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                    flex-direction: column;
                    align-items: flex-start;
                    justify-content: flex-start;
                }
            `,
        }),
    },
    header: {
        overflowMenu: {
            cssOverrides: {
                wrapper: css`
                    margin-left: 20px;
                `,
            },
        },
        removeAllLink: {
            ...createLinkIconStyles("X"),
            css: css`
                margin-right: 12px;
                flex-shrink: 0;
                ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                    margin-right: 0;
                }
            `,
        },
        mainSection: {
            css: css`
                display: flex;
                align-items: center;
                padding-bottom: 5px;
            `,
        },
        itemCountText: {
            ...mergeWithCss(cartLineSharedStyles.header?.title, {
                css: css`
                    margin-right: 40px;
                    ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                        margin-right: 0;
                        margin-bottom: 20px;
                    }
                `,
            }),
        },
    },
    cartLinesActions: {
        css: css`
            display: flex;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                flex-direction: row;
                gap: 32px;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.xs)} {
                flex-direction: column;
                gap: 16px;
            }
        `,
    },
    cartLineActionsWrapper: {
        css: css`
            display: flex;
            flex-grow: 1;
            align-items: flex-end;
            justify-content: flex-end;
            width: 100%;
        `,
    },
};

const StyledSection = getStyledWrapper("section");

const styles = cartLinesStyles;

const CartLines: FC<Props> = ({
    cart,
    promotionsDataView,
    settingsCollection,
    clearCurrentCart,
    fields,
    isClearingCart,
    isUpdatingCartLine,
    isLoading,
    isRemovingCartLine,
    showPricing,
    showInventory,
    updateCartLine,
    removeCartLine,
    isAuthenticated,
    isNonPurchasable,
    xCommonLoadingOverlay,
}) => {
    // XNG-Changes
    // const [isCondensed, setIsCondensed] = useState(false);
    const [isRemoveAllModalOpen, setIsRemoveAllModalOpen] = useState(false);

    // XNG-Changes
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    if (!cart || (cart.lineCount && !cart.cartLines) || isClearingCart) {
        return (
            <StyledSection {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledSection>
        );
    }

    const cartLines = cart.cartLines || [];

    if (cartLines.length === 0) {
        return (
            <XngLoadingOverlayWrapper {...styles.loadingOverlay} id="CartLinesWidget" loading={false}>
                <StyledSection {...styles.centeringWrapper}>
                    <Icon {...styles.noCartLinesIcon} />
                    <Typography {...styles.noCartLinesText} data-test-selector="cart_noCartLinesMessage">
                        {isAuthenticated && isNonPurchasable
                            ? siteMessage("Keller_Cart_NoOrderLines_WebOnly")
                            : siteMessage("Cart_NoOrderLines")}
                    </Typography>
                </StyledSection>
            </XngLoadingOverlayWrapper>
        );
    }

    // const isCondensedChangeHandler: CheckboxProps["onChange"] = (_, value) => {
    //     setIsCondensed(value);
    // };

    const removeAllClickHandler = () => {
        if (cart.status === "AwaitingApproval") {
            setIsRemoveAllModalOpen(true);
        } else {
            // XNG-Changes
            // clearCurrentCart();
            setShowConfirmationModal(true);
        }
    };

    const submitRemoveAllClickHandler = () => {
        setIsRemoveAllModalOpen(false);
        clearCurrentCart();
    };

    const { productSettings } = settingsCollection;
    const { value: promotions } = promotionsDataView;
    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive || o.cantBuy);
    const isLoadingCart =
        isLoading[cart.id] || (cart.status === "AlternateCart" && isLoading[API_URL_CURRENT_FRAGMENT]);

    const cartLinesDisplay = cartLines.map((cartLine, index) => {
        if (!showPricing && cartLine.isPromotionItem) {
            return;
        }
        const matchingPromotions = promotions ? promotions.filter(promo => promo.orderLineId === cartLine.id) : [];
        const showRemoveAction = !cartLine.isPromotionItem && cart.canModifyOrder;
        return (
            <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                {/* XNG-Changes - Remove the CartLineCardCondensed */}
                {/* {isCondensed ? (
                    <CartLineCardCondensed
                        cart={cart}
                        promotions={matchingPromotions}
                        productSettings={productSettings}
                        showInventoryAvailability={showInventory}
                        extendedStyles={styles.cardCondensed}
                        updateCartLine={updateCartLine}
                        removeCartLine={removeCartLine}
                        showRemoveAction={showRemoveAction}
                        showVmiBinDetails={fields.showVmiBinDetails}
                    />
                ) */}
                <CartLineCardExpanded
                    cart={cart}
                    promotions={matchingPromotions}
                    // XNG-Changes
                    // productSettings={productSettings}
                    showInventoryAvailability={showInventory}
                    showLineNotes={fields.showLineNotes}
                    showImage={fields.showImage}
                    showProductTitle={fields.showProductTitle}
                    showBrand={fields.showBrand}
                    showPerUnitPrice={fields.showPerUnitPrice}
                    showPriceLabel={fields.showPriceLabel}
                    showAddToList={fields.showAddToList}
                    showProductNumber={fields.showProductNumber}
                    // XNG-Changes
                    // extendedStyles={styles.cardExpanded}
                    // updateCartLine={updateCartLine}
                    // removeCartLine={removeCartLine}
                    // showRemoveAction={showRemoveAction}
                    showVmiBinDetails={fields.showVmiBinDetails}
                    // XNG-Changes - New
                    showSavingsPercent={productSettings.showSavingsPercent}
                    showSavingsAmount={productSettings.showSavingsAmount}
                    updateCartLine={updateCartLine}
                    removeCartLine={removeCartLine}
                    showRemoveAction={showRemoveAction}
                    editable={true}
                    editableCostCode={true}
                    className={`${index === 0 ? "first-item" : ""}`}
                    extendedStyles={styles.cardExpanded}
                />
            </CartLineContext.Provider>
        );
    });

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledSection {...styles.wrapper}>
                <XngLoadingOverlayWrapper
                    {...styles.loadingOverlay}
                    id="CartLinesWidget"
                    loading={isLoadingCart || isUpdatingCartLine || promotionsDataView.isLoading || isRemovingCartLine}
                >
                    <CartLinesHeader
                        onRemoveAllClick={removeAllClickHandler}
                        // productsCannotBePurchased={productsCannotBePurchased}
                        // cartNotPriced={cart.cartNotPriced}
                        // hasInsufficientInventory={cart.hasInsufficientInventory}
                        // hasInvalidPrice={hasProductsWithInvalidPrice(cart)}
                        // failedToGetRealTimeInventory={cart.failedToGetRealTimeInventory}
                        showItemCount={fields.showItemCount}
                        showRemoveAllActions={fields.showRemoveAllActions}
                        fulfillmentMethod={cart.fulfillmentMethod}
                    />
                    {cartLinesDisplay}
                    {/* {fields.showBackToTop !== false && <CartLinesFooter />} */}
                    <TwoButtonModal
                        headlineText={translate("Are you sure?")}
                        modalIsOpen={isRemoveAllModalOpen}
                        messageText={siteMessage("OrderApproval_RemoveAllConfirmationMessage")}
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Clear cart and delete order")}
                        onCancel={() => setIsRemoveAllModalOpen(false)}
                        onSubmit={submitRemoveAllClickHandler}
                    ></TwoButtonModal>
                </XngLoadingOverlayWrapper>
                <TwoButtonModal
                    headlineText={translate("Confirms Line Removal")}
                    modalIsOpen={showConfirmationModal}
                    messageText={siteMessage("Keller_Cart_RemoveAllCartLines")}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Delete")}
                    onCancel={() => setShowConfirmationModal(false)}
                    onSubmit={() => {
                        clearCurrentCart();
                        setShowConfirmationModal(false);
                    }}
                    submitTestSelector="RemoveAllCartLines"
                    dataTestSelector="twoButtonModal_RemoveAllCartLines"
                ></TwoButtonModal>
            </StyledSection>
        </StyledWrapper>
    );
};

interface CartLinesHeaderProps {
    // XNG-Changes - update totalItemCount and isCondensed to optional props
    totalItemCount?: number;
    isCondensed?: boolean;
    productsCannotBePurchased?: boolean;
    cartNotPriced?: boolean;
    hasInsufficientInventory?: boolean;
    hasInvalidPrice?: boolean;
    failedToGetRealTimeInventory?: boolean;
    // XNG-Changes - optional props
    onIsCondensedChange?: CheckboxProps["onChange"];
    onRemoveAllClick: () => void;
    showItemCount?: boolean;
    showRemoveAllActions?: boolean;
    fulfillmentMethod?: string;
}

const CartLinesHeader: FC<CartLinesHeaderProps> = ({
    productsCannotBePurchased,
    cartNotPriced,
    hasInsufficientInventory,
    hasInvalidPrice,
    failedToGetRealTimeInventory,
    onRemoveAllClick,
    showItemCount = true,
    showRemoveAllActions = true,
    fulfillmentMethod,
}) => {
    const headerStyles = styles.header || {};

    return (
        <>
            <StyledSection {...styles.headerWrapper} id="cartHeader">
                <Typography {...headerStyles.itemCountText}>{translate("Products")}</Typography>
                <StyledWrapper {...styles.cartLineActionsWrapper}>
                    <StyledWrapper {...styles.cartLinesActions}>
                        {createWidgetElement("Cart/CartActions", {
                            fields: {},
                        })}
                    </StyledWrapper>
                    {showRemoveAllActions && (
                        <Link onClick={onRemoveAllClick} {...headerStyles.removeAllLink}>
                            {translate("Remove All")}
                        </Link>
                    )}
                </StyledWrapper>
                {/* {productsCannotBePurchased && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography
                        {...headerStyles.warningText}
                        data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                    >
                        {siteMessage("Cart_ProductsCannotBePurchased")}
                    </Typography>
                </StyledSection>
            )}
            {cartNotPriced && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography
                        {...headerStyles.warningText}
                        data-test-selector="cartlineHeader_NoPriceAvailableAtCheckout"
                    >
                        {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                    </Typography>
                </StyledSection>
            )}
            {hasInsufficientInventory && fulfillmentMethod === "Ship" && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography
                        {...headerStyles.warningText}
                        data-test-selector="cartlineHeader_warningInsufficientInventoryAtCheckout"
                    >
                        {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                    </Typography>
                </StyledSection>
            )}
            {hasInsufficientInventory && fulfillmentMethod === "PickUp" && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography
                        {...headerStyles.warningText}
                        data-test-selector="cartlineHeader_warningInsufficientPickupInventory"
                    >
                        {siteMessage("Cart_InsufficientPickupInventory")}
                    </Typography>
                </StyledSection>
            )}
            {!cartNotPriced && hasInvalidPrice && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography {...headerStyles.warningText}>{siteMessage("Cart_InvalidPriceAtCheckout")}</Typography>
                </StyledSection>
            )}
            {failedToGetRealTimeInventory && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography {...headerStyles.warningText}>
                        {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                    </Typography>
                </StyledSection>
            )} */}
            </StyledSection>
            <LinesHeader />
        </>
    );
};

// const CartLinesFooter: FC = () => {
//     const backToTopClickHandler = () => {
//         const header = document.getElementById("cartHeader");
//         if (header) {
//             window.scrollTo(0, header.offsetTop);
//         }
//     };

//     return (
//         <StyledSection {...styles.footerWrapper}>
//             <Button onClick={backToTopClickHandler} {...styles.backToTopButton}>
//                 {translate("Back to Top")}
//             </Button>
//         </StyledSection>
//     );
// };

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartLines),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.showLineNotes,
                displayName: "Show Line Notes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                tooltip: "This setting does not apply to Punchout orders, which will never display Line Notes.",
                tab: basicTab,
                sortOrder: 0,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 1,
            },
            {
                name: fields.showProductTitle,
                displayName: "Product title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 3,
            },
            {
                name: fields.showRemoveAllActions,
                displayName: "Remove all actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 4,
            },
            {
                name: fields.showPerUnitPrice,
                displayName: "Per unit price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 5,
            },
            {
                name: fields.showPriceLabel,
                displayName: "Price label",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 6,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to list",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 7,
            },
            {
                name: fields.showItemCount,
                displayName: "Item count",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 8,
            },
            {
                name: fields.showProductNumber,
                displayName: "Product number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 9,
            },
            {
                name: fields.showBackToTop,
                displayName: "Back to top",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 10,
            },
            {
                name: fields.showVmiBinDetails,
                displayName: "VMI Bin details",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 11,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
